/** Tax Reports page for sales tax and VAT reporting. */
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Refresh,
  Download,
  PictureAsPdf,
  TableChart,
} from "@mui/icons-material";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";

interface TaxReportData {
  jurisdiction: string;
  tax_type: string;
  taxable_amount: number;
  tax_collected: number;
  transactions: number;
}

interface SummaryCard {
  title: string;
  value: string;
  subtitle: string;
  color: string;
}

/** Tax Reports page component. */
const TaxReports: React.FC = () => {
  const [reportPeriod, setReportPeriod] = useState("last_month");
  const [taxType, setTaxType] = useState("all");
  const [jurisdiction, setJurisdiction] = useState("all");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });

  // Tax report data - in production, this would come from the backend API
  // The backend would aggregate this from invoices based on tax rules
  const summaryCards: SummaryCard[] = [
    {
      title: "Total Tax Collected",
      value: "$12,458.32",
      subtitle: "Current period",
      color: "#4caf50",
    },
    {
      title: "Taxable Revenue",
      value: "$156,479.00",
      subtitle: "Current period",
      color: "#2196f3",
    },
    {
      title: "Effective Tax Rate",
      value: "7.96%",
      subtitle: "Weighted average",
      color: "#ff9800",
    },
    {
      title: "Transactions",
      value: "2,847",
      subtitle: "Taxable transactions",
      color: "#9c27b0",
    },
  ];

  const mockReportData: TaxReportData[] = [
    {
      jurisdiction: "California",
      tax_type: "Sales Tax",
      taxable_amount: 45600.00,
      tax_collected: 3650.40,
      transactions: 845,
    },
    {
      jurisdiction: "New York",
      tax_type: "Sales Tax",
      taxable_amount: 38200.00,
      tax_collected: 3056.00,
      transactions: 623,
    },
    {
      jurisdiction: "Texas",
      tax_type: "Sales Tax",
      taxable_amount: 29800.00,
      tax_collected: 2384.00,
      transactions: 512,
    },
    {
      jurisdiction: "Florida",
      tax_type: "Sales Tax",
      taxable_amount: 25100.00,
      tax_collected: 1757.00,
      transactions: 445,
    },
    {
      jurisdiction: "EU VAT",
      tax_type: "VAT",
      taxable_amount: 17779.00,
      tax_collected: 1610.92,
      transactions: 422,
    },
  ];

  const handleRefresh = () => {
    setLoading(true);
    // In production, fetch actual tax report data from API
    // GET /api/v1/billing/tax-reports?period=last_month&tax_type=all&jurisdiction=all
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleExportPDF = () => {
    // In production, this would call the backend to generate a PDF
    // GET /api/v1/billing/tax-reports/export?format=pdf&period=last_month
    setNotification({
      open: true,
      message: "PDF export started. Download will begin shortly.",
    });
    setTimeout(() => setNotification({ open: false, message: "" }), 3000);
  };

  const handleExportCSV = () => {
    // Generate CSV export
    const headers = ["Jurisdiction", "Tax Type", "Taxable Amount", "Tax Collected", "Tax Rate", "Transactions"];
    const rows = mockReportData.map((row) => {
      const taxRate = ((row.tax_collected / row.taxable_amount) * 100).toFixed(2);
      return [
        row.jurisdiction,
        row.tax_type,
        row.taxable_amount.toFixed(2),
        row.tax_collected.toFixed(2),
        taxRate,
        row.transactions.toString(),
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `tax-report-${reportPeriod}-${Date.now()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setNotification({
      open: true,
      message: "CSV export downloaded successfully.",
    });
    setTimeout(() => setNotification({ open: false, message: "" }), 3000);
  };

  const getDateRangeLabel = () => {
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    switch (reportPeriod) {
      case "this_month":
        return `${format(now, "MMMM yyyy")}`;
      case "last_month":
        return `${format(lastMonth, "MMMM yyyy")}`;
      case "this_quarter":
        return `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`;
      case "this_year":
        return `${now.getFullYear()}`;
      default:
        return "Select period";
    }
  };

  const filteredData = mockReportData.filter((row) => {
    if (taxType !== "all" && row.tax_type.toLowerCase().replace(".", "") !== taxType) {
      return false;
    }
    if (jurisdiction === "us" && !["California", "New York", "Texas", "Florida"].includes(row.jurisdiction)) {
      return false;
    }
    if (jurisdiction === "eu" && row.jurisdiction !== "EU VAT") {
      return false;
    }
    if (jurisdiction === "ca" && !row.jurisdiction.includes("Canada")) {
      return false;
    }
    return true;
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Tax Reports
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={reportPeriod}
              label="Period"
              onChange={(e) => setReportPeriod(e.target.value)}
            >
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="last_month">Last Month</MenuItem>
              <MenuItem value="this_quarter">This Quarter</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Tax Type</InputLabel>
            <Select
              value={taxType}
              label="Tax Type"
              onChange={(e) => setTaxType(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="salestax">Sales Tax</MenuItem>
              <MenuItem value="vat">VAT</MenuItem>
              <MenuItem value="gst">GST</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Jurisdiction</InputLabel>
            <Select
              value={jurisdiction}
              label="Jurisdiction"
              onChange={(e) => setJurisdiction(e.target.value)}
            >
              <MenuItem value="all">All Jurisdictions</MenuItem>
              <MenuItem value="us">United States</MenuItem>
              <MenuItem value="eu">European Union</MenuItem>
              <MenuItem value="ca">Canada</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Info Alert about tax reports */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Tax reports are generated from invoice transactions based on configured tax rules per customer.
        Use the Settings page to configure tax rates for different jurisdictions.
      </Alert>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="h5" component="div" sx={{ color: card.color, fontWeight: "bold" }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tax Breakdown Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Tax Breakdown - {getDateRangeLabel()}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChart />}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
              >
                Export PDF
              </Button>
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Jurisdiction</TableCell>
                  <TableCell>Tax Type</TableCell>
                  <TableCell align="right">Taxable Amount</TableCell>
                  <TableCell align="right">Tax Collected</TableCell>
                  <TableCell align="right">Tax Rate</TableCell>
                  <TableCell align="right">Transactions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length > 0 ? (
                  <>
                    {filteredData.map((row) => {
                      const taxRate = ((row.tax_collected / row.taxable_amount) * 100).toFixed(2);
                      return (
                        <TableRow key={`${row.jurisdiction}-${row.tax_type}`}>
                          <TableCell>{row.jurisdiction}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.tax_type}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            ${row.taxable_amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell align="right">
                            ${row.tax_collected.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell align="right">{taxRate}%</TableCell>
                          <TableCell align="right">{row.transactions}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={{ "&:hover": { bgcolor: "action.hover" }, fontWeight: "bold" }}>
                      <TableCell colSpan={2}>Total</TableCell>
                      <TableCell align="right">
                        ${filteredData.reduce((sum, r) => sum + r.taxable_amount, 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="right">
                        ${filteredData.reduce((sum, r) => sum + r.tax_collected, 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="right">
                        {((filteredData.reduce((sum, r) => sum + r.tax_collected, 0) / filteredData.reduce((sum, r) => sum + r.taxable_amount, 0)) * 100).toFixed(2)}%
                      </TableCell>
                      <TableCell align="right">
                        {filteredData.reduce((sum, r) => sum + r.transactions, 0)}
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No tax data found for the selected filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ open: false, message: "" })}
        message={notification.message}
      />
    </Container>
  );
};

export default TaxReports;
